import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from "@mui/material";
import Swal from "sweetalert2";
import DanaAvailabilityCalendar from "../../components/Calendar/DanaAvailabilityCalendar";
import RequestPageMUI from "./RequestPageMUI";
import apiClient from "@/api/client";

const DanaGilanpasaAvailability = () => {
  const [calendarData, setCalendarData] = useState({ booked: [], blocked: [] });
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [blocking, setBlocking] = useState(false);
  const [error, setError] = useState(null);

  // Fetch calendar data (booked + blocked dates)
  const fetchCalendarData = async () => {
    setCalendarLoading(true);
    setError(null);
    try {
      const response = await apiClient.get("/get-gilan-pasa-calendar");
      const data = response.data;

      if (data.success) {
        setCalendarData({
          booked: data.data.booked || [],
          blocked: data.data.blocked || [],
        });
      } else {
        throw new Error(data.message || "Failed to fetch calendar data");
      }
    } catch (err) {
      console.error("Error fetching calendar data:", err);
      setError("Failed to load calendar data. Please try again.");
    } finally {
      setCalendarLoading(false);
    }
  };

  // Handle date selection from calendar
  const handleDateSelect = (date) => {
    // Check if date is already booked
    const isBooked = calendarData.booked.some(
      (item) => (item.date || item.dana_event_date) === date
    );

    if (isBooked) {
      Swal.fire({
        icon: "warning",
        title: "Date Already Booked",
        text: "This date is already booked for Gilan Pasa. Please reject the booking first before blocking.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    // Check if date is already blocked
    const isBlocked = calendarData.blocked.some(
      (item) => (item.date || item.dana_event_date) === date
    );

    if (isBlocked) {
      Swal.fire({
        icon: "info",
        title: "Date Already Blocked",
        text: "This date is already blocked for Gilan Pasa.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    // Open block dialog
    setSelectedDate(date);
    setBlockReason("");
    setBlockDialogOpen(true);
  };

  // Handle blocking a date
  const handleBlockDate = async () => {
    if (!blockReason.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Reason Required",
        text: "Please enter a reason for blocking this date.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    setBlocking(true);
    try {
      const response = await apiClient.post("/block-gilan-pasa-date", {
        date: selectedDate,
        reason: blockReason.trim(),
      });

      const data = response.data;

      if (data.success) {
        Swal.fire({
          icon: "success",
          title: "Date Blocked",
          text: "The date has been successfully blocked.",
          confirmButtonColor: "#3085d6",
        });

        setBlockDialogOpen(false);
        setSelectedDate(null);
        setBlockReason("");
        fetchCalendarData();
      } else {
        throw new Error(data.message || "Failed to block date");
      }
    } catch (err) {
      console.error("Error blocking date:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to Block Date",
        text:
          err.response?.data?.message ||
          err.message ||
          "An error occurred while blocking the date.",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setBlocking(false);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="h5" component="div">
                Gilan Pasa - Availability Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                - Manage Gilan Pasa availability. Click on an available date to block it.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        {/* Calendar Section */}
        <Box
          sx={{
            width: { xs: "100%", md: "420px" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent
              sx={{
                p: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: "text.primary",
                    fontSize: "1.1rem",
                    fontWeight: 550,
                    textAlign: "center",
                  }}
                >
                  Calendar View
                </Typography>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: "text.secondary",
                    fontSize: "1rem",
                    fontWeight: 500,
                    textAlign: "left",
                    pl: 3,
                  }}
                >
                  Tap on a date to block
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {calendarLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <DanaAvailabilityCalendar
                    onDateSelect={handleDateSelect}
                    bookedDates={calendarData.booked}
                    blockedDates={calendarData.blocked}
                    showLegend={true}
                    onRefresh={fetchCalendarData}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Data Grid Section */}
        <Box sx={{ flex: { xs: "1", md: "1.5" }, minWidth: 0 }}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: 2,
              }}
            >
              <Box sx={{ pl: 3, mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="h6" sx={{ color: "text.primary" }}>
                    Blocked Gilan Pasa Dates Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    - View and manage all blocked Gilan Pasa dates.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ flex: 1, minHeight: 0, mt: 1 }}>
                <RequestPageMUI
                  title="Blocked Dates"
                  fetchUrl="/get-gilan-pasa-blocked-dates-grid"
                  editEndpoint="update-gilan-pasa-blocked-date"
                  deleteEndpoint="delete-gilan-pasa-blocked-date"
                  statusEndpoint=""
                  dateFields={["date", "created_at", "updated_at"]}
                  hiddenFields={[]}
                  filterField={null}
                  filterValue={null}
                  showFilter={false}
                  serverSideSorting={true}
                  enableColumnReorder={true}
                  persistToDatabase={true}
                  tableId="gilanpasa_blocked_dates"
                  customColumns={null}
                  additionalActions={null}
                  onRowClick={null}
                  pageSize={15}
                  height="100%"
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Block Date Dialog */}
      <Dialog
        open={blockDialogOpen}
        onClose={() => !blocking && setBlockDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Block Gilan Pasa Date</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to block <strong>{selectedDate}</strong> for Gilan Pasa.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This will prevent any new Gilan Pasa requests for this date.
          </Typography>

          <TextField
            label="Reason for Blocking"
            fullWidth
            multiline
            rows={3}
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder="Enter reason for blocking this date (e.g., Special event, Maintenance, etc.)"
            disabled={blocking}
            sx={{ mt: 1 }}
          />

          {blocking && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="textSecondary">
                Blocking date...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)} disabled={blocking}>
            Cancel
          </Button>
          <Button
            onClick={handleBlockDate}
            variant="contained"
            disabled={!blockReason.trim() || blocking}
          >
            {blocking ? "Blocking..." : "Block Date"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DanaGilanpasaAvailability;